import { Box, Collapse, Typography, useTheme } from '@mui/material';
import { AnimatePresence } from 'framer-motion';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { MAX_FOLDER_DEPTH } from '../../../../constants/folder';
import { useSidebarTreeStore } from '../../../../store/storeSidebarTree';
import type { Namespace } from '../../../../types/namespace';
import { Folder } from '../../../../util/Types/Folder';
import { FolderItemHeader } from '../AgentsSidebar/FolderItemHeader';
import { FolderItemMenu } from '../AgentsSidebar/FolderItemMenu';
import type { FluxObject } from '../AgentsSidebar/context';
import { useFolderLocalContent } from '../AgentsSidebar/useFolderLocalContent';
import { AnimatedListItem } from '../dnd/AnimatedListItem';
import { DraggableItemShell } from '../dnd/DraggableItemShell';
import { dropHighlightSx } from '../dnd/dropHighlightSx';
import {
  buildFolderDragData,
  getReservedFolderType,
  isLockedFolder,
} from '../dnd/dropRules';
import { getNodeDepth } from '../dnd/folderDepth';
import { buildSidebarChildren } from '../dnd/sidebarChildren';
import type { FolderDropData, ReservedFolderType } from '../dnd/types';
import { useCombinedDndRef } from '../dnd/useCombinedDndRef';
import { useDraggableNode } from '../dnd/useDraggableNode';
import { useDropTargetNode } from '../dnd/useDropTargetNode';
import { useExpandOnHover } from '../dnd/useExpandOnHover';
import { isVirtualFolderId } from '../utils';
import { ObjectItem } from './ObjectItem';
import { VectorMemoryItem } from './VectorMemoryItem';

interface DataFolderItemProps {
  folder: Folder;
  ancestorReservedType?: ReservedFolderType | null;
  spaceId: string | null;
  onObjectEdit: (obj: FluxObject) => void;
  onObjectDelete: (obj: FluxObject) => void;
  onVectorEdit: (ns: Namespace) => void;
  onVectorDelete: (ns: Namespace) => void;
  onFolderRename?: (folder: Folder) => void;
  onFolderDelete?: (folder: Folder) => void;
  onAddSubfolder?: (folder: Folder) => void;
  onAddItem?: (folder: Folder) => void;
}

export const DataFolderItem: React.FC<DataFolderItemProps> = ({
  folder,
  ancestorReservedType = null,
  spaceId,
  onObjectEdit,
  onObjectDelete,
  onVectorEdit,
  onVectorDelete,
  onFolderRename,
  onFolderDelete,
  onAddSubfolder,
  onAddItem,
}) => {
  const theme = useTheme();
  const palette = theme.customColorPalette;
  const isDarkMode = theme.palette.mode === 'dark';
  const [isOpen, setIsOpen] = useState(false);
  const [localFolder] = useFolderLocalContent(folder);
  const [isHovered, setIsHovered] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const folderHeaderRef = useRef<HTMLDivElement | null>(null);
  const depth = useSidebarTreeStore(s => getNodeDepth(s.nodes, folder.id) || 1);

  const reservedType = getReservedFolderType(folder);
  const isReserved = reservedType !== null;
  const isLocked = isLockedFolder(folder);
  const isVirtual = isVirtualFolderId(folder.id);
  const folderLocked = isLocked || isVirtual;
  const dragDisabled = folderLocked || isReserved;
  const dropDisabled = isLocked || isReserved;

  const resolvedSpaceId = spaceId || folder.space || null;
  const dragData = useMemo(
    () => buildFolderDragData(folder, ancestorReservedType, resolvedSpaceId),
    [folder, ancestorReservedType, resolvedSpaceId],
  );
  const draggable = useDraggableNode({
    data: dragData,
    label: folder.name,
    disabled: dragDisabled,
  });
  const dropData: FolderDropData = useMemo(
    () => ({
      kind: 'folder-drop',
      folderId: folder.id,
      parentId: folder.parentId ?? null,
      spaceId: resolvedSpaceId,
      reservedType,
      ancestorReservedType,
      isVirtual,
      isLocked: folderLocked,
    }),
    [
      folder,
      folderLocked,
      ancestorReservedType,
      isVirtual,
      reservedType,
      resolvedSpaceId,
    ],
  );
  const dropTarget = useDropTargetNode({
    data: dropData,
    mode: 'folder',
    disabled: dropDisabled,
  });

  const showDropAbove = dropTarget.position === 'above';
  const showDropBelow = dropTarget.position === 'below';
  const showDropInside = dropTarget.position === 'inside' && dropTarget.isOver;

  const headerRef = useCombinedDndRef(
    folderHeaderRef,
    draggable.setRef,
    dropTarget.setRef,
  );

  useExpandOnHover({
    isOver: dropTarget.isOver,
    isOpen,
    onExpand: useCallback(() => setIsOpen(true), []),
  });

  const canAddSubfolder =
    !isReserved &&
    !isVirtual &&
    !isLocked &&
    !!onAddSubfolder &&
    depth < MAX_FOLDER_DEPTH;
  const hasFolderActions =
    !isReserved &&
    !isVirtual &&
    (onFolderRename || onFolderDelete || canAddSubfolder);

  const handleMenuOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (folderHeaderRef.current) setMenuAnchor(folderHeaderRef.current);
  };

  const handleMenuClose = () => setMenuAnchor(null);

  const handleToggle = () => setIsOpen(prev => !prev);

  const childAncestor = reservedType ?? ancestorReservedType ?? null;

  const subfolders = localFolder.folders ?? [];
  const objects = localFolder.objects ?? [];
  const namespaces = localFolder.namespaces ?? [];
  const children = useMemo(
    () => buildSidebarChildren({ subfolders, objects, namespaces }),
    [subfolders, objects, namespaces],
  );
  const hasItems = children.length > 0;

  const dropHighlight = dropHighlightSx(showDropInside, palette.blue[500]);

  return (
    <DraggableItemShell
      isDragging={draggable.isDragging}
      style={dropHighlight}
      dropAbove={showDropAbove}
      dropBelow={showDropBelow}
    >
      <FolderItemHeader
        folder={folder}
        isOpen={isOpen}
        isReserved={false}
        isHovered={isHovered}
        showAddAgent={!!onAddItem && !folderLocked}
        showMenuButton={!!hasFolderActions}
        menuOpen={!!menuAnchor}
        headerRef={headerRef}
        onToggle={handleToggle}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onAddAgent={onAddItem}
        onMenuOpen={handleMenuOpen}
      />
      {menuAnchor && (
        <FolderItemMenu
          folder={folder}
          anchorEl={menuAnchor}
          onClose={handleMenuClose}
          onRename={onFolderRename}
          onDelete={onFolderDelete}
          canAddSubfolder={canAddSubfolder}
          onAddSubfolder={onAddSubfolder}
        />
      )}
      <Collapse in={isOpen}>
        <Box sx={{ paddingLeft: '14px' }}>
          {!hasItems ? (
            <Typography
              sx={{
                color: isDarkMode ? palette.gray[500] : palette.gray[400],
                fontFamily: 'Roboto',
                fontSize: '12px',
                fontStyle: 'italic',
                padding: '8px 16px',
              }}
            >
              No items
            </Typography>
          ) : (
            <AnimatePresence initial={false}>
              {children.map(child => {
                if (child.kind === 'folder') {
                  return (
                    <AnimatedListItem
                      key={`folder:${child.id}`}
                      itemKey={child.id}
                    >
                      <DataFolderItem
                        folder={child.node}
                        ancestorReservedType={childAncestor}
                        spaceId={spaceId}
                        onObjectEdit={onObjectEdit}
                        onObjectDelete={onObjectDelete}
                        onVectorEdit={onVectorEdit}
                        onVectorDelete={onVectorDelete}
                        onFolderRename={onFolderRename}
                        onFolderDelete={onFolderDelete}
                        onAddSubfolder={onAddSubfolder}
                        onAddItem={onAddItem}
                      />
                    </AnimatedListItem>
                  );
                }
                if (child.kind === 'object') {
                  return (
                    <AnimatedListItem
                      key={`object:${child.id}`}
                      itemKey={child.id}
                    >
                      <ObjectItem
                        object={child.node}
                        spaceId={spaceId}
                        ancestorReservedType={childAncestor}
                        onEdit={onObjectEdit}
                        onDelete={onObjectDelete}
                        isNested
                      />
                    </AnimatedListItem>
                  );
                }
                if (child.kind === 'vector') {
                  return (
                    <AnimatedListItem key={`ns:${child.id}`} itemKey={child.id}>
                      <VectorMemoryItem
                        namespace={child.node}
                        spaceId={spaceId}
                        ancestorReservedType={childAncestor}
                        onEdit={onVectorEdit}
                        onDelete={onVectorDelete}
                        isNested
                      />
                    </AnimatedListItem>
                  );
                }
                return null;
              })}
            </AnimatePresence>
          )}
        </Box>
      </Collapse>
    </DraggableItemShell>
  );
};
